import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { useUpdateCart } from '~/api';
import { BundleQuantityEditor } from '~/components/common/BundleQuantityEditor';
import { CellEditPopover } from '~/components/ui/table/CellEditPopover';
import type { GetCartsWithFilterQuery, Quantity } from '~/graphql/graphql';
import { ProductLengthGrid } from '~/components/common/ProductLengthGrid';
import { formatUnitPrice, priceUnitLabel, toastGraphQLError } from '~/utils';
import {
  calcUnitsPerBundleForProductLength,
  unitsPerPieceFor,
  wholeBundlePieces,
} from '~/components/ui/bundleQuantity';
import { usePreferredUnit } from '~/hooks/usePreferredUnit';
import { convertPricePerUnit, pricedUnitFor } from '~/utils/unitConversion';
import {
  buildCartItemUpdateInput,
  getCartLinePrice,
  getPlatformProductDimensions,
  piecesForQuantity,
} from '~/utils/order';

export type BuyerCart = NonNullable<GetCartsWithFilterQuery['carts']>[number];
type CartItem = NonNullable<BuyerCart['items']>[number];

export type CartTarget = {
  organizationId: string;
  facilityId: string;
  locationId: string;
};

type CartLinesProps = {
  cart: BuyerCart;
  target: CartTarget;
};

function lengthKeyWithinTally(lengthId: string, tallyId?: string): string {
  return `${lengthId}:${tallyId ?? ''}`;
}

function toGridItem(item: CartItem) {
  return {
    id: lengthKeyWithinTally(
      item.platformProductsLengths.id,
      item.tallyPlatformProduct?.id
    ),
    quantity: item.quantity,
    effectivePiecesPerBundle: item.effectivePiecesPerBundle,
    platformProductsLength: item.platformProductsLengths,
    cartItem: item,
  };
}

type GridItem = ReturnType<typeof toGridItem>;

export function LinePrice({
  item,
  incoTerm,
  currency,
}: Readonly<{
  item: CartItem;
  incoTerm: BuyerCart['incoTerm'];
  currency: BuyerCart['currency'];
}>) {
  const { t } = useTranslation('cart');
  const { platformProduct, length, isNominal } = item.platformProductsLengths;
  const preferredUnit = usePreferredUnit(
    platformProduct.category,
    item.quantity.unit
  );
  const dims = getPlatformProductDimensions(platformProduct, length, isNominal);
  const price = convertPricePerUnit(
    getCartLinePrice(item, incoTerm),
    preferredUnit,
    item.quantity.unit,
    dims
  );
  if (price == null) {
    return <span className="text-neut-sec">{t('cart.new_cart.no_price')}</span>;
  }
  return (
    <>
      {formatUnitPrice(price, currency ?? undefined)} /
      {priceUnitLabel(pricedUnitFor(preferredUnit, item.quantity.unit, dims))}
    </>
  );
}

type CartLineActionsProps = {
  line: GridItem;
  rowIndex: number;
  target: CartTarget;
  isEditing: boolean;
  onOpen: () => void;
  onClose: () => void;
};

function CartLineActions({
  line,
  rowIndex,
  target,
  isEditing,
  onOpen,
  onClose,
}: Readonly<CartLineActionsProps>) {
  const { t } = useTranslation('cart');
  const { mutate: updateCart, isPending: isSaving } = useUpdateCart();
  const { facilityId, locationId } = target;

  const saveQuantity = (
    quantity: Quantity,
    newPiecesPerBundle: number | undefined
  ) => {
    const { effectivePiecesPerBundle, platformProductsLengths } = line.cartItem;
    const piecesPerBundle = newPiecesPerBundle ?? effectivePiecesPerBundle;
    const isPiecesPerBundleChanged =
      piecesPerBundle !== effectivePiecesPerBundle;
    const pieces = piecesForQuantity(
      quantity.value,
      piecesPerBundle,
      calcUnitsPerBundleForProductLength(
        line.quantity,
        piecesPerBundle,
        platformProductsLengths
      ),
      unitsPerPieceFor(platformProductsLengths, line.quantity.unit)
    );
    const sendQuantity = isPiecesPerBundleChanged
      ? wholeBundlePieces(quantity, piecesPerBundle, platformProductsLengths)
      : {
          value: pieces ?? quantity.value,
          unit: pieces == null ? quantity.unit : 'PCS',
        };
    updateCart(
      buildCartItemUpdateInput({
        productId: line.platformProductsLength.id,
        locationId,
        facilityId,
        quantity: sendQuantity.value,
        unit: sendQuantity.unit,
        piecesPerBundle: isPiecesPerBundleChanged ? piecesPerBundle : undefined,
      }),
      {
        onSuccess: onClose,
        onError: toastGraphQLError,
      }
    );
  };

  const removeLine = () =>
    updateCart(
      buildCartItemUpdateInput({
        productId: line.platformProductsLength.id,
        locationId,
        facilityId,
        quantity: 0,
        unit: line.cartItem.quantity.unit,
      }),
      { onError: toastGraphQLError }
    );

  const removeLabel = t('cart.new_cart.remove_line', {
    product: line.platformProductsLength.platformProduct.name,
  });
  return (
    <>
      <CellEditPopover
        isOpen={isEditing}
        onOpen={onOpen}
        onDone={onClose}
        isBusy={isSaving}
        columnLabel={t('cart.new_cart.quantity')}
        testId={`cart-line-edit-quantity-${rowIndex}`}
        anchorClassName="text-neut-600"
        renderEditor={() => (
          <BundleQuantityEditor
            quantity={line.quantity}
            platformProductsLength={line.cartItem.platformProductsLengths}
            piecesPerBundle={line.cartItem.effectivePiecesPerBundle}
            isSaving={isSaving}
            onSave={(quantity, piecesPerBundle) =>
              saveQuantity(quantity, piecesPerBundle)
            }
            onCancel={onClose}
            testId="cart-line"
          />
        )}
      />
      <button
        type="button"
        className="text-neut-600 hover:text-error-700 cursor-pointer disabled:opacity-50"
        aria-label={removeLabel}
        title={removeLabel}
        data-testid={`cart-line-remove-${rowIndex}`}
        disabled={isSaving}
        onClick={() => removeLine()}
      >
        <Trash2 size={14} aria-hidden="true" />
      </button>
    </>
  );
}

export function CartLines({ cart, target }: Readonly<CartLinesProps>) {
  const { t } = useTranslation('cart');
  const [editingId, setEditingId] = useState<string | null>(null);

  const items = cart.items ?? [];
  if (items.length === 0) {
    return (
      <p className="text-sm text-neut-sec">{t('cart.new_cart.no_lines')}</p>
    );
  }

  return (
    <ProductLengthGrid
      items={items.map(toGridItem)}
      testId="new-cart-lines"
      renderPrice={(line) => (
        <LinePrice
          item={line.cartItem}
          incoTerm={cart.incoTerm}
          currency={cart.currency}
        />
      )}
      renderActions={(line, rowIndex) =>
        line.cartItem.tallyPlatformProduct ? null : (
          <CartLineActions
            line={line}
            rowIndex={rowIndex}
            target={target}
            isEditing={editingId === line.id}
            onOpen={() => setEditingId(line.id)}
            onClose={() => setEditingId(null)}
          />
        )
      }
    />
  );
}
